import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { 
  Users, TrendingUp, TrendingDown, Calendar, Briefcase, Clock, 
  CheckCircle2, AlertTriangle, Award, FileText, Activity, Target,
  UserPlus, UserMinus, Building2, FileCheck, Loader2, Download, ArrowRight
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import api from '../services/api';
import { toast } from 'sonner';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

const MOIS_NOMS = [
  '', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
];

const SyntheseCollective = ({ zone }) => {
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [synthese, setSynthese] = useState(null);
  
  // Période sélectionnée (plage de mois)
  const [moisDebut, setMoisDebut] = useState(new Date().getMonth() + 1);
  const [anneeDebut, setAnneeDebut] = useState(new Date().getFullYear());
  const [moisFin, setMoisFin] = useState(new Date().getMonth() + 1);
  const [anneeFin, setAnneeFin] = useState(new Date().getFullYear());

  useEffect(() => {
    loadSynthese();
  }, [moisDebut, anneeDebut, moisFin, anneeFin, zone]);

  const loadSynthese = async () => {
    setLoading(true);
    try {
      const zoneParam = zone ? `&zone=${zone}` : '';
      const response = await api.get(
        `/stats/synthese-collective?mois_debut=${moisDebut}&annee_debut=${anneeDebut}&mois_fin=${moisFin}&annee_fin=${anneeFin}${zoneParam}`
      );
      setSynthese(response.data);
    } catch (error) {
      console.error('Erreur chargement synthèse:', error);
      toast.error('Erreur lors du chargement de la synthèse');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      const zoneParam = zone ? `&zone=${zone}` : '';
      const response = await api.get(
        `/stats/synthese-collective/pdf?mois_debut=${moisDebut}&annee_debut=${anneeDebut}&mois_fin=${moisFin}&annee_fin=${anneeFin}${zoneParam}`,
        { responseType: 'blob' }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Nom du fichier avec la période
      const periodeLabel = moisDebut === moisFin && anneeDebut === anneeFin
        ? `${MOIS_NOMS[moisDebut]}_${anneeDebut}`
        : `${MOIS_NOMS[moisDebut]}_${anneeDebut}_a_${MOIS_NOMS[moisFin]}_${anneeFin}`;
      link.setAttribute('download', `Synthese_Collective_${periodeLabel}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Synthèse téléchargée avec succès');
    } catch (error) {
      console.error('Erreur téléchargement:', error);
      toast.error('Erreur lors du téléchargement');
    } finally {
      setDownloading(false);
    }
  };

  // Générer les labels de période pour l'affichage
  const getPeriodeLabel = () => {
    if (moisDebut === moisFin && anneeDebut === anneeFin) {
      return `${MOIS_NOMS[moisDebut]} ${anneeDebut}`;
    }
    return `${MOIS_NOMS[moisDebut]} ${anneeDebut} → ${MOIS_NOMS[moisFin]} ${anneeFin}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-3 text-slate-600">Chargement de la synthèse...</span>
      </div>
    );
  }

  if (!synthese) {
    return (
      <div className="text-center py-8 text-slate-500">
        <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-slate-300" />
        <p>Impossible de charger la synthèse</p>
      </div>
    );
  }

  const { indicateurs_cles, focus_recrutement, activites_realisees, analyse_qualitative, periode } = synthese;

  // Données pour les graphiques
  const thematiquesData = activites_realisees.thematiques.map((t, i) => ({
    name: t.nom?.replace(/_/g, ' ') || 'Autre',
    count: t.count,
    participants: t.participants,
    fill: COLORS[i % COLORS.length]
  }));

  const actionsData = activites_realisees.types_actions.map((t, i) => ({
    name: t.nom?.replace(/_/g, ' ') || 'Autre',
    count: t.count,
    heures: t.heures,
    fill: COLORS[i % COLORS.length]
  }));

  return (
    <div className="space-y-6">
      {/* En-tête avec sélecteurs de période */}
      <Card className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-3xl border-0 shadow-xl">
        <CardContent className="p-6">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h2 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
                  <FileText className="w-7 h-7" />
                  Synthèse Collective
                </h2>
                <p className="text-blue-100 mt-1">
                  {getPeriodeLabel()} {zone ? `- Zone: ${zone}` : ''}
                </p>
              </div>
              <Button 
                onClick={handleDownloadPDF}
                disabled={downloading}
                className="bg-white/20 hover:bg-white/30 text-white rounded-xl flex items-center gap-2"
                data-testid="download-synthese-pdf"
              >
                {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                Télécharger PDF
              </Button>
            </div>
            
            {/* Sélecteurs de période (plage de mois) */}
            <div className="flex flex-wrap items-center gap-3 bg-white/10 rounded-xl p-3">
              <Label className="text-blue-100 text-sm">Du :</Label>
              <Select value={moisDebut.toString()} onValueChange={(v) => setMoisDebut(parseInt(v))}>
                <SelectTrigger className="w-[130px] bg-white/20 border-white/30 text-white rounded-lg text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MOIS_NOMS.slice(1).map((nom, i) => (
                    <SelectItem key={i + 1} value={(i + 1).toString()}>{nom}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={anneeDebut.toString()} onValueChange={(v) => setAnneeDebut(parseInt(v))}>
                <SelectTrigger className="w-[90px] bg-white/20 border-white/30 text-white rounded-lg text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[2024, 2025, 2026].map((a) => (
                    <SelectItem key={a} value={a.toString()}>{a}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <ArrowRight className="w-5 h-5 text-blue-200" />
              
              <Label className="text-blue-100 text-sm">Au :</Label>
              <Select value={moisFin.toString()} onValueChange={(v) => setMoisFin(parseInt(v))}>
                <SelectTrigger className="w-[130px] bg-white/20 border-white/30 text-white rounded-lg text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MOIS_NOMS.slice(1).map((nom, i) => (
                    <SelectItem key={i + 1} value={(i + 1).toString()}>{nom}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={anneeFin.toString()} onValueChange={(v) => setAnneeFin(parseInt(v))}>
                <SelectTrigger className="w-[90px] bg-white/20 border-white/30 text-white rounded-lg text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[2024, 2025, 2026].map((a) => (
                    <SelectItem key={a} value={a.toString()}>{a}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* INDICATEURS CLÉS */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
        <Card className="bg-white rounded-2xl shadow-lg border-0 hover:shadow-xl transition-shadow">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Bénéficiaires suivis</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">{indicateurs_cles.total_beneficiaires}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white rounded-2xl shadow-lg border-0 hover:shadow-xl transition-shadow">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Nouveaux entrants</p>
                <p className="text-3xl font-bold text-emerald-600 mt-1">+{indicateurs_cles.nouveaux_entrants}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                <UserPlus className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white rounded-2xl shadow-lg border-0 hover:shadow-xl transition-shadow">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Sorties</p>
                <p className="text-3xl font-bold text-amber-600 mt-1">{indicateurs_cles.sorties}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                <UserMinus className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white rounded-2xl shadow-lg border-0 hover:shadow-xl transition-shadow">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Taux de placement</p>
                <p className="text-3xl font-bold text-rose-600 mt-1">{indicateurs_cles.taux_placement || focus_recrutement?.taux_placement || 0}%</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-rose-100 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-rose-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white rounded-2xl shadow-lg border-0 hover:shadow-xl transition-shadow">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Taux de présence</p>
                <p className="text-3xl font-bold text-purple-600 mt-1">{indicateurs_cles.taux_presence}%</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                <Target className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white rounded-2xl shadow-lg border-0 hover:shadow-xl transition-shadow">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wide">Événements</p>
                <p className="text-3xl font-bold text-indigo-600 mt-1">{indicateurs_cles.total_evenements}</p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center">
                <Calendar className="w-6 h-6 text-indigo-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* RÉPARTITION HOMME/FEMME - Ligne séparée */}
      <Card className="bg-white rounded-2xl shadow-lg border-0">
        <CardContent className="p-5">
          <p className="text-xs text-slate-500 uppercase tracking-wide mb-3">Répartition Homme / Femme</p>
          <div className="flex items-center justify-center gap-8 flex-wrap">
            {/* Hommes */}
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                <span className="text-xl">👨</span>
              </div>
              <div>
                <p className="text-3xl font-bold text-blue-600">{indicateurs_cles.repartition_genre?.hommes || 0}</p>
                <p className="text-sm text-slate-500">Hommes ({indicateurs_cles.repartition_genre?.pourcentage_hommes || 0}%)</p>
              </div>
            </div>
            
            {/* Barre de répartition */}
            <div className="flex-1 max-w-md min-w-[200px]">
              <div className="flex h-6 rounded-full overflow-hidden bg-slate-100">
                <div 
                  className="bg-blue-500 transition-all duration-500 flex items-center justify-center text-white text-xs font-medium" 
                  style={{ width: `${indicateurs_cles.repartition_genre?.pourcentage_hommes || 0}%` }}
                >
                  {indicateurs_cles.repartition_genre?.pourcentage_hommes > 10 && `${indicateurs_cles.repartition_genre?.pourcentage_hommes}%`}
                </div>
                <div 
                  className="bg-pink-500 transition-all duration-500 flex items-center justify-center text-white text-xs font-medium" 
                  style={{ width: `${indicateurs_cles.repartition_genre?.pourcentage_femmes || 0}%` }}
                >
                  {indicateurs_cles.repartition_genre?.pourcentage_femmes > 10 && `${indicateurs_cles.repartition_genre?.pourcentage_femmes}%`}
                </div>
                {indicateurs_cles.repartition_genre?.non_renseigne > 0 && (
                  <div 
                    className="bg-slate-300 transition-all duration-500 flex items-center justify-center text-slate-600 text-xs" 
                    style={{ width: `${100 - (indicateurs_cles.repartition_genre?.pourcentage_hommes || 0) - (indicateurs_cles.repartition_genre?.pourcentage_femmes || 0)}%` }}
                  >
                    ?
                  </div>
                )}
              </div>
              {indicateurs_cles.repartition_genre?.non_renseigne > 0 && (
                <p className="text-xs text-slate-400 text-center mt-2">
                  {indicateurs_cles.repartition_genre.non_renseigne} non renseigné(s)
                </p>
              )}
            </div>
            
            {/* Femmes */}
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-pink-100 flex items-center justify-center">
                <span className="text-xl">👩</span>
              </div>
              <div>
                <p className="text-3xl font-bold text-pink-600">{indicateurs_cles.repartition_genre?.femmes || 0}</p>
                <p className="text-sm text-slate-500">Femmes ({indicateurs_cles.repartition_genre?.pourcentage_femmes || 0}%)</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* FOCUS RECRUTEMENT */}
      <Card className="bg-white rounded-3xl shadow-lg border-0">
        <CardHeader className="pb-2">
          <CardTitle className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
            <Briefcase className="w-6 h-6 text-emerald-600" />
            Focus Recrutement
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Stats recrutement */}
            <div className="flex flex-col gap-4">
              <div className="bg-emerald-50 rounded-2xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                    <FileCheck className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-emerald-700">{focus_recrutement.total_contrats_mois}</p>
                    <p className="text-sm text-emerald-600">Contrats signés ce mois</p>
                  </div>
                </div>
              </div>
              <div className="bg-amber-50 rounded-2xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                    <Clock className="w-6 h-6 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-amber-700">{focus_recrutement.demandes_en_cours}</p>
                    <p className="text-sm text-amber-600">Demandes en cours</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Liste des contrats signés */}
            <div className="lg:col-span-2">
              <h4 className="font-semibold text-slate-700 mb-3">Contrats signés ce mois</h4>
              {focus_recrutement.contrats_signes.length === 0 ? (
                <div className="text-center py-6 text-slate-500 bg-slate-50 rounded-xl">
                  <Briefcase className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                  <p>Aucun contrat signé ce mois</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-slate-50">
                        <th className="text-left p-3 text-xs font-semibold text-slate-600 uppercase">Bénéficiaire</th>
                        <th className="text-left p-3 text-xs font-semibold text-slate-600 uppercase">Poste</th>
                        <th className="text-left p-3 text-xs font-semibold text-slate-600 uppercase">Entreprise</th>
                        <th className="text-left p-3 text-xs font-semibold text-slate-600 uppercase">Type</th>
                      </tr>
                    </thead>
                    <tbody>
                      {focus_recrutement.contrats_signes.map((contrat, i) => (
                        <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="p-3 font-medium text-slate-900">{contrat.beneficiaire_nom}</td>
                          <td className="p-3 text-slate-600">{contrat.poste}</td>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              <Building2 className="w-4 h-4 text-slate-400" />
                              {contrat.entreprise}
                            </div>
                          </td>
                          <td className="p-3">
                            <Badge className={`${
                              contrat.type_contrat === 'CDI' ? 'bg-emerald-100 text-emerald-700' :
                              contrat.type_contrat === 'CDD' ? 'bg-blue-100 text-blue-700' :
                              'bg-slate-100 text-slate-700'
                            }`}>
                              {contrat.type_contrat}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ACTIVITÉS RÉALISÉES */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Thématiques des ateliers */}
        <Card className="bg-white rounded-3xl shadow-lg border-0">
          <CardHeader className="pb-2">
            <CardTitle className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
              <Activity className="w-6 h-6 text-blue-600" />
              Thématiques Ateliers
              <Badge className="bg-blue-100 text-blue-700 ml-2">{activites_realisees.total_ateliers} ateliers</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {thematiquesData.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Activity className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                <p>Aucun atelier ce mois</p>
              </div>
            ) : (
              <div className="space-y-3">
                {thematiquesData.map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.fill }}></div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-slate-700 capitalize">{item.name}</span>
                        <span className="text-sm text-slate-500">{item.count} session(s) - {item.participants} participants</span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-2 mt-1">
                        <div 
                          className="h-2 rounded-full transition-all duration-500" 
                          style={{ 
                            width: `${Math.min(100, (item.count / Math.max(...thematiquesData.map(t => t.count))) * 100)}%`,
                            backgroundColor: item.fill 
                          }}
                        ></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Types d'actions individuelles */}
        <Card className="bg-white rounded-3xl shadow-lg border-0">
          <CardHeader className="pb-2">
            <CardTitle className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
              <Clock className="w-6 h-6 text-purple-600" />
              Actions Individuelles
              <Badge className="bg-purple-100 text-purple-700 ml-2">{activites_realisees.total_actions} RDV</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {actionsData.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Clock className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                <p>Aucune action ce mois</p>
              </div>
            ) : (
              <div className="space-y-3">
                {actionsData.map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.fill }}></div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-slate-700 capitalize">{item.name}</span>
                        <span className="text-sm text-slate-500">{item.count} RDV - {item.heures}h</span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-2 mt-1">
                        <div 
                          className="h-2 rounded-full transition-all duration-500" 
                          style={{ 
                            width: `${Math.min(100, (item.count / Math.max(...actionsData.map(t => t.count))) * 100)}%`,
                            backgroundColor: item.fill 
                          }}
                        ></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Volume horaire global */}
      <Card className="bg-gradient-to-r from-indigo-500 to-purple-600 text-white rounded-3xl border-0 shadow-xl">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center">
                <Clock className="w-8 h-8" />
              </div>
              <div>
                <p className="text-indigo-100 text-sm uppercase tracking-wide">Volume horaire global</p>
                <p className="text-4xl font-bold">{activites_realisees.volume_horaire_global}h</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-indigo-100">Accompagnement total</p>
              <p className="text-xl">{activites_realisees.total_ateliers} ateliers + {activites_realisees.total_actions} RDV</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ANALYSE QUALITATIVE */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Points de blocage */}
        <Card className="bg-white rounded-3xl shadow-lg border-0">
          <CardHeader className="pb-2">
            <CardTitle className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
              <AlertTriangle className="w-6 h-6 text-amber-500" />
              Points de Blocage
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {analyse_qualitative.points_blocage.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-emerald-300" />
                <p>Aucun frein majeur identifié</p>
              </div>
            ) : (
              <div className="space-y-3">
                {analyse_qualitative.points_blocage.map((frein, i) => {
                  const beneficiaires = frein.beneficiaires || [];
                  return (
                    <div key={i} className="p-3 bg-amber-50 rounded-xl">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center text-amber-700 font-bold">
                            {i + 1}
                          </div>
                          <span className="font-medium text-slate-700 capitalize">
                            {frein.frein?.replace(/_/g, ' ') || frein.frein}
                          </span>
                        </div>
                        <Badge className="bg-amber-100 text-amber-700">
                          {frein.count} bénéficiaire{frein.count > 1 ? 's' : ''}
                        </Badge>
                      </div>
                      {/* Liste des noms des bénéficiaires */}
                      {beneficiaires.length > 0 && (
                        <div className="pl-11 text-sm text-slate-600">
                          {beneficiaires.slice(0, 5).map((b, idx) => (
                            <span key={b.id || idx}>
                              {b.prenom} {b.nom}{idx < Math.min(beneficiaires.length, 5) - 1 ? ', ' : ''}
                            </span>
                          ))}
                          {beneficiaires.length > 5 && (
                            <span className="text-slate-400"> +{beneficiaires.length - 5} autres</span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Succès marquants */}
        <Card className="bg-white rounded-3xl shadow-lg border-0">
          <CardHeader className="pb-2">
            <CardTitle className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
              <Award className="w-6 h-6 text-emerald-500" />
              Succès du Mois
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-emerald-50 rounded-2xl p-4 text-center">
                <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center mx-auto mb-2">
                  <FileCheck className="w-6 h-6 text-emerald-600" />
                </div>
                <p className="text-2xl font-bold text-emerald-700">{analyse_qualitative.succes_marquants.contrats_signes}</p>
                <p className="text-sm text-emerald-600">Contrats signés</p>
              </div>
              <div className="bg-blue-50 rounded-2xl p-4 text-center">
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center mx-auto mb-2">
                  <CheckCircle2 className="w-6 h-6 text-blue-600" />
                </div>
                <p className="text-2xl font-bold text-blue-700">{analyse_qualitative.succes_marquants.freins_leves}</p>
                <p className="text-sm text-blue-600">Freins levés</p>
              </div>
              <div className="bg-purple-50 rounded-2xl p-4 text-center">
                <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center mx-auto mb-2">
                  <Briefcase className="w-6 h-6 text-purple-600" />
                </div>
                <p className="text-2xl font-bold text-purple-700">{analyse_qualitative.succes_marquants.beneficiaires_places}</p>
                <p className="text-sm text-purple-600">Bénéficiaires placés</p>
              </div>
              <div className="bg-indigo-50 rounded-2xl p-4 text-center">
                <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center mx-auto mb-2">
                  <Target className="w-6 h-6 text-indigo-600" />
                </div>
                <p className="text-2xl font-bold text-indigo-700">{analyse_qualitative.succes_marquants.taux_presence}%</p>
                <p className="text-sm text-indigo-600">Taux de présence</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SyntheseCollective;
